import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Switch,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea
} from '@tonic/shared/components/ui';
import { Save } from 'lucide-react';

const mockSettings = {
  general: {
    autoAssignTechnicians: true,
    requirePhotos: true,
    maxActiveRepairsPerTechnician: 5,
    defaultPriorityLevel: 'medium'
  },
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    notifyCustomerOnStatus: true,
    notifyTechnicianOnAssignment: true
  },
  scheduling: {
    allowWeekendAppointments: false,
    minLeadTime: 24,
    maxLeadTime: 168,
    appointmentDuration: 60
  },
  quality: {
    requireQualityCheck: true,
    qualityCheckersRequired: 1,
    photoDocumentationRequired: true,
    followUpSurvey: true
  }
};

export function RepairSettingsPage() {
  const [settings, setSettings] = React.useState(mockSettings);
  const [isDirty, setIsDirty] = React.useState(false);

  const handleChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    console.log('Saving settings:', settings);
    // Add save logic here
    setIsDirty(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Repair Settings</h1>
          <p className="text-sm text-gray-500">Configure repair service operations</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!isDirty}
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Auto-assign Technicians</p>
                <p className="text-sm text-gray-500">Automatically assign repairs to available technicians</p>
              </div>
              <Switch
                checked={settings.general.autoAssignTechnicians}
                onCheckedChange={(checked) => handleChange('general', 'autoAssignTechnicians', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Require Photos</p>
                <p className="text-sm text-gray-500">Require photos for repair documentation</p>
              </div>
              <Switch
                checked={settings.general.requirePhotos}
                onCheckedChange={(checked) => handleChange('general', 'requirePhotos', checked)}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Max Active Repairs per Technician</p>
              <Input
                type="number"
                value={settings.general.maxActiveRepairsPerTechnician}
                onChange={(e) => handleChange('general', 'maxActiveRepairsPerTechnician', parseInt(e.target.value))}
                min={1}
                max={10}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Default Priority Level</p>
              <Select
                value={settings.general.defaultPriorityLevel}
                onValueChange={(value) => handleChange('general', 'defaultPriorityLevel', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-500">Send email updates for repair status changes</p>
              </div>
              <Switch
                checked={settings.notifications.emailNotifications}
                onCheckedChange={(checked) => handleChange('notifications', 'emailNotifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-gray-500">Send SMS updates for repair status changes</p>
              </div>
              <Switch
                checked={settings.notifications.smsNotifications}
                onCheckedChange={(checked) => handleChange('notifications', 'smsNotifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Notify Customer on Status Change</p>
                <p className="text-sm text-gray-500">Send notifications when repair status changes</p>
              </div>
              <Switch
                checked={settings.notifications.notifyCustomerOnStatus}
                onCheckedChange={(checked) => handleChange('notifications', 'notifyCustomerOnStatus', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Notify Technician on Assignment</p>
                <p className="text-sm text-gray-500">Send notifications when repairs are assigned</p>
              </div>
              <Switch
                checked={settings.notifications.notifyTechnicianOnAssignment}
                onCheckedChange={(checked) => handleChange('notifications', 'notifyTechnicianOnAssignment', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Scheduling Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Scheduling Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Allow Weekend Appointments</p>
                <p className="text-sm text-gray-500">Enable scheduling repairs on weekends</p>
              </div>
              <Switch
                checked={settings.scheduling.allowWeekendAppointments}
                onCheckedChange={(checked) => handleChange('scheduling', 'allowWeekendAppointments', checked)}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Minimum Lead Time (hours)</p>
              <Input
                type="number"
                value={settings.scheduling.minLeadTime}
                onChange={(e) => handleChange('scheduling', 'minLeadTime', parseInt(e.target.value))}
                min={1}
                max={72}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Maximum Lead Time (hours)</p>
              <Input
                type="number"
                value={settings.scheduling.maxLeadTime}
                onChange={(e) => handleChange('scheduling', 'maxLeadTime', parseInt(e.target.value))}
                min={24}
                max={336}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Default Appointment Duration (minutes)</p>
              <Input
                type="number"
                value={settings.scheduling.appointmentDuration}
                onChange={(e) => handleChange('scheduling', 'appointmentDuration', parseInt(e.target.value))}
                min={30}
                max={240}
                step={15}
              />
            </div>
          </CardContent>
        </Card>

        {/* Quality Control Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Quality Control Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Require Quality Check</p>
                <p className="text-sm text-gray-500">Require quality inspection before completion</p>
              </div>
              <Switch
                checked={settings.quality.requireQualityCheck}
                onCheckedChange={(checked) => handleChange('quality', 'requireQualityCheck', checked)}
              />
            </div>

            <div>
              <p className="font-medium mb-2">Quality Checkers Required</p>
              <Input
                type="number"
                value={settings.quality.qualityCheckersRequired}
                onChange={(e) => handleChange('quality', 'qualityCheckersRequired', parseInt(e.target.value))}
                min={1}
                max={3}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Photo Documentation Required</p>
                <p className="text-sm text-gray-500">Require photos for quality control</p>
              </div>
              <Switch
                checked={settings.quality.photoDocumentationRequired}
                onCheckedChange={(checked) => handleChange('quality', 'photoDocumentationRequired', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Follow-up Survey</p>
                <p className="text-sm text-gray-500">Send customer satisfaction survey after completion</p>
              </div>
              <Switch
                checked={settings.quality.followUpSurvey}
                onCheckedChange={(checked) => handleChange('quality', 'followUpSurvey', checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 
